import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Loader2, LogOut, User } from "lucide-react";
import { accountApi } from "@/api/account";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ProfilePage() {
  const { session, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => accountApi.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => { setCurrent(""); setNext(""); setConfirm(""); setLocalError(null); },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (next !== confirm) { setLocalError("Az új jelszavak nem egyeznek."); return; }
    m.mutate();
  }

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
        <User className="size-5 text-primary" /> Profil
      </h1>

      <Card>
        <CardContent className="flex items-center gap-3 p-4">
          <div className="grid size-12 place-items-center rounded-full bg-primary/15 text-primary">
            <User className="size-6" />
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium">{session?.fullName}</div>
            <div className="truncate text-sm text-muted-foreground">{session?.userName}</div>
          </div>
          {session?.isAdmin && <span className="ml-auto rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning">Admin</span>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><KeyRound className="size-4" /> Jelszó módosítása</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="cur">Jelenlegi jelszó</Label>
              <Input id="cur" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new">Új jelszó</Label>
              <Input id="new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cf">Új jelszó újra</Label>
              <Input id="cf" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            {(localError || m.error) && (
              <p className="text-sm text-destructive">{localError ?? (m.error instanceof ApiError ? m.error.message : "Sikertelen módosítás.")}</p>
            )}
            {m.isSuccess && <p className="text-sm text-success">A jelszó megváltozott.</p>}
            <Button type="submit" disabled={m.isPending}>
              {m.isPending && <Loader2 className="size-4 animate-spin" />} Mentés
            </Button>
          </form>
        </CardContent>
      </Card>

      <Button variant="outline" className="w-full" onClick={logout}>
        <LogOut className="size-4" /> Kijelentkezés
      </Button>
    </div>
  );
}
